import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ComicCard from '@/components/base/ComicCard';
import { comics, genres } from '@/mocks/comics';
import { useSeo } from '@/hooks/useSeo';

const sorts = [
  { key: 'heat', label: '按热度' },
  { key: 'rating', label: '按评分' },
  { key: 'chapters', label: '按章节数' },
];

export default function Genre() {
  const [params, setParams] = useSearchParams();
  const initialType = params.get('type') || '全部';
  const initialQ = params.get('q') || '';
  const [active, setActive] = useState(initialType);
  const [keyword, setKeyword] = useState(initialQ);
  const [sort, setSort] = useState('heat');

  useSeo({
    title: '动漫之家漫画网分类大全 - 按类型浏览漫画',
    description:
      '动漫之家漫画网分类大全，热血、恋爱、玄幻、悬疑、搞笑、日常、科幻等类型一应俱全，按分类快速浏览并找到你想追的漫画作品。',
    canonicalPath: '/genre',
  });

  const changeType = (g: string) => {
    setActive(g);
    setParams(g === '全部' ? {} : { type: g });
  };

  const list = useMemo(() => {
    let base = comics;
    if (active !== '全部') base = base.filter((c) => c.genre === active || c.tags.includes(active));
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      base = base.filter(
        (c) => c.title.toLowerCase().includes(kw) || c.author.toLowerCase().includes(kw) || c.description.toLowerCase().includes(kw),
      );
    }
    return [...base].sort((a, b) => (sort === 'heat' ? b.heat - a.heat : sort === 'rating' ? b.rating - a.rating : b.chapters - a.chapters));
  }, [active, keyword, sort]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setParams({ ...(active !== '全部' ? { type: active } : {}), ...(keyword.trim() ? { q: keyword.trim() } : {}) });
  };

  return (
    <div>
      {/* Page head */}
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">分类大全</span>
          </nav>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-600">
                <i className="ri-apps-2-line"></i> 分类大全
              </span>
              <h1 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">按类型浏览漫画</h1>
              <p className="mt-2 text-sm text-foreground-500">选择分类或直接搜索，快速找到你感兴趣的作品。</p>
            </div>
            <form onSubmit={handleSearch} className="flex max-w-xs items-center rounded-full border border-background-300 bg-background-50 px-4 py-2.5" role="search">
              <i className="ri-search-line text-sm text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画或作者…"
                className="w-full bg-transparent px-2 text-sm outline-none placeholder:text-foreground-400"
                aria-label="分类搜索"
              />
            </form>
          </div>
        </div>
      </section>

      {/* Genre tabs */}
      <section className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10" aria-label="分类漫画列表">
        <div className="flex flex-wrap gap-2">
          {genres.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => changeType(g)}
              className={`rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap ${
                active === g ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {g === '全部' ? '全部' : `${g}漫画`}
            </button>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <span className="flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-price-tag-3-line text-primary-500"></i>
            {active}分类下共 <span className="font-semibold text-foreground-900">{list.length}</span> 部作品
          </span>
          <div className="inline-flex rounded-full border border-background-300 bg-background-50 p-1">
            {sorts.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSort(s.key)}
                className={`rounded-full px-3 py-1.5 text-xs transition-colors whitespace-nowrap ${
                  sort === s.key ? 'bg-accent-500 text-background-50' : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {list.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {list.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-10 text-center">
            <i className="ri-emotion-sad-line text-4xl text-background-400"></i>
            <p className="mt-3 text-sm text-foreground-600">没有找到相关作品，试试切换分类或更换关键词。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setActive('全部');
                setParams({});
              }}
              className="mt-4 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              查看全部
            </button>
          </div>
        )}
      </section>
    </div>
  );
}